export const WORKSPACE_STORAGE = 'yj_admin_workspace_v1';
export type WorkspaceRoute = { key: string; label: string };
export type WorkspaceTab = { key: string; href: string; visited: boolean };
export type ViewState = Record<string, unknown>;
const aliases: Record<string, string> = {
  '/identities': '/people?tab=identities', '/teachers': '/people?tab=teachers',
  '/cards': '/students', '/enrollment-qr': '/schools?tab=qrs',
  '/devices': '/hardware-access?tab=devices', '/attendance-hardware': '/hardware-access?tab=attendance',
  '/call-terminal-hardware': '/hardware-access?tab=phone'
};
export function canonicalHref(href: string) {
  const url = new URL(href, 'https://workspace.invalid');
  return aliases[url.pathname] || `${url.pathname}${url.search}`;
}
export function routeKey(href: string, routes: WorkspaceRoute[]) {
  const path = canonicalHref(href).split('?')[0];
  return routes.find(r => r.key !== '/' && (path === r.key || path.startsWith(`${r.key}/`)))?.key || '/';
}
const safeParams = new Set(['schoolId', 'gradeId', 'classId', 'status', 'guardianStatus', 'tab']);
const safeFields = new Set(['schoolId', 'gradeId', 'classId', 'status', 'guardianStatus', 'paymentStatus', 'month', 'type']);
const safeString = (value: unknown): value is string => typeof value === 'string' && /^[a-zA-Z0-9_-]{0,100}$/.test(value);
export function safeHref(href: string) {
  const url = new URL(canonicalHref(href), 'https://workspace.invalid');
  for (const [key, value] of Array.from(url.searchParams)) {
    if (!safeParams.has(key) || !safeString(value)) url.searchParams.delete(key);
  }
  return `${url.pathname}${url.search}`;
}
export function safeViewState(state: ViewState): ViewState {
  const result: ViewState = {};
  // Explicit opt-in: never persist names, order numbers, photos, selections or form drafts.
  for (const [key, value] of Object.entries(state)) {
    if (!/^(students|orders|production)\.(page|filters|tab)$/.test(key)) continue;
    if (key.endsWith('.page') && Number.isInteger(value) && Number(value) > 0 && Number(value) <= 100000) result[key] = value;
    if (key === 'production.tab' && ['INITIAL', 'REPLACEMENT', 'BATCHES'].includes(String(value))) result[key] = value;
    if (key.endsWith('.filters') && value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = Object.fromEntries(Object.entries(value).filter(([field, val]) => safeFields.has(field) && safeString(val)));
    }
  }
  return result;
}
export function restoreWorkspace(raw: string | null, owner: string, routes: WorkspaceRoute[], incomingHref?: string) {
  const initial = { tabs: [{ key: '/', href: '/', visited: false }] as WorkspaceTab[], views: {} as Record<string, ViewState> };
  try {
    const parsed = JSON.parse(raw || 'null');
    if (parsed?.version !== 1 || parsed.owner !== owner || !Array.isArray(parsed.tabs)) return initial;
    for (const item of parsed.tabs.slice(0, routes.length)) {
      if (!item || typeof item.href !== 'string' || !routes.some(r => r.key === item.key) || initial.tabs.some(t => t.key === item.key)) continue;
      const href = safeHref(item.href);
      if (routeKey(href, routes) !== item.key) continue;
      initial.tabs.push({ key: item.key, href, visited: false });
      initial.views[item.key] = safeViewState(parsed.views?.[item.key] || {});
    }
    // An explicit new deep link wins over a previous visit's cached list filters/page.
    if (incomingHref) {
      const key = routeKey(incomingHref, routes);
      const existing = initial.tabs.find(tab => tab.key === key);
      if (existing && existing.href !== safeHref(incomingHref)) delete initial.views[key];
    }
  } catch { /* A corrupt or unavailable browser cache must not block the admin. */ }
  return initial;
}
export function clearWorkspaceStorage() {
  try { sessionStorage.removeItem(WORKSPACE_STORAGE); } catch { /* Storage may be disabled. */ }
}
